// /Frontend/src/pages/AdminCompanies.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Server, LogOut, Settings, Building2, Plus, Trash2,
  CheckCircle, XCircle, Clock, ChevronDown, ChevronUp,
  AlertCircle, Users, Globe, Package, ArrowLeft, Edit2, X,
  Shield, ShieldOff
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import AdminNav from '../components/AdminNav';
import HomeButton from '../components/HomeButton';
// ─── Modal ────────────────────────────────────────────────────────────────────
const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
    <div className="relative w-full max-w-lg bg-slate-900 border border-slate-700 rounded-2xl p-6 shadow-2xl">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-white">{title}</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
          <X className="w-5 h-5" />
        </button>
      </div>
      {children}
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    active:    { color: 'bg-green-500/10 text-green-400 border-green-500/20',    label: 'Active' },
    pending:   { color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20', label: 'Pending' },
    rejected:  { color: 'bg-red-500/10 text-red-400 border-red-500/20',          label: 'Rejected' },
    suspended: { color: 'bg-slate-500/10 text-slate-400 border-slate-500/20',    label: 'Suspended' },
  };
  const s = map[status] || map.active;
  return <span className={`px-3 py-1 rounded-full text-xs font-medium border ${s.color}`}>{s.label}</span>;
};

const PlanBadge = ({ plan }) => {
  const map = {
    standard:   'bg-blue-500/10 text-blue-400 border-blue-500/20',
    pro:        'bg-purple-500/10 text-purple-400 border-purple-500/20',
    enterprise: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
  };
  return <span className={`px-2 py-0.5 rounded text-xs font-medium border ${map[plan] || map.standard} capitalize`}>{plan}</span>;
};

const RoleBadge = ({ role }) => {
  if (role === 'company_admin') {
    return <span className="px-2 py-0.5 rounded text-xs font-medium border bg-purple-500/10 text-purple-400 border-purple-500/20">Company Admin</span>;
  }
  return <span className="px-2 py-0.5 rounded text-xs font-medium border bg-slate-500/10 text-slate-400 border-slate-500/20">User</span>;
};

const CompanyForm = ({ form, setForm, formError, formLoading, onSubmit, submitLabel, onCancel }) => (
  <form onSubmit={onSubmit} className="space-y-4">
    {formError && (
      <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center space-x-2">
        <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
        <p className="text-red-400 text-sm">{formError}</p>
      </div>
    )}
    <div className="grid grid-cols-2 gap-4">
      <div className="col-span-2">
        <label className="block text-sm font-medium text-slate-300 mb-1">Company Name *</label>
        <input type="text" required value={form.name}
          onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
          placeholder="company-name"
          className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors text-sm" />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-1">Namespace</label>
        <input type="text" value={form.namespace}
          onChange={e => setForm(p => ({ ...p, namespace: e.target.value }))}
          placeholder="company-namespace"
          className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors text-sm font-mono" />
        <p className="text-xs text-slate-500 mt-1">ArgoCD namespace</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-1">Plan</label>
        <select value={form.plan} onChange={e => setForm(p => ({ ...p, plan: e.target.value }))}
          className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-blue-500 transition-colors text-sm">
          <option value="standard">Standard</option>
          <option value="pro">Pro</option>
          <option value="enterprise">Enterprise</option>
        </select>
      </div>
      <div className="col-span-2">
        <label className="block text-sm font-medium text-slate-300 mb-1">Notes</label>
        <textarea value={form.notes} rows={2}
          onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
          placeholder="Optional notes about this company..."
          className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors text-sm resize-none" />
      </div>
    </div>
    <div className="flex space-x-3 pt-2">
      <button type="submit" disabled={formLoading}
        className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-blue-600 hover:to-cyan-500 text-white font-medium py-2.5 rounded-lg transition-all disabled:opacity-50 flex items-center justify-center space-x-2 text-sm">
        {formLoading
          ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /><span>Saving...</span></>
          : <span>{submitLabel}</span>}
      </button>
      <button type="button" onClick={onCancel}
        className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors text-sm">
        Cancel
      </button>
    </div>
  </form>
);

// ─── Main component ───────────────────────────────────────────────────────────
const AdminCompanies = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [companies, setCompanies]             = useState([]);
  const [pendingUsers, setPendingUsers]       = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [activeTab, setActiveTab]             = useState('companies');
  const [expandedCompany, setExpandedCompany] = useState(null);
  const [companyUsers, setCompanyUsers]       = useState({});
  const [deletingUserId, setDeletingUserId]   = useState(null);

  const [showAddModal, setShowAddModal]   = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editTarget, setEditTarget]       = useState(null);
  const [formError, setFormError]         = useState('');
  const [formLoading, setFormLoading]     = useState(false);

  const emptyForm = { name: '', namespace: '', plan: 'standard', notes: '' };
  const [form, setForm] = useState(emptyForm);

  const isSuperAdmin = user?.role === 'admin';

  const fetchData = async () => {
    try {
      const [companiesRes, pendingRes] = await Promise.all([
        api.get('/admin/companies'),
        api.get('/admin/companies/users/pending'),
      ]);
      setCompanies(companiesRes.data.companies || []);
      setPendingUsers(pendingRes.data.users || []);
    } catch (err) {
      console.error('Error fetching companies data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const toggleExpand = async (companyId) => {
    if (expandedCompany === companyId) { setExpandedCompany(null); return; }
    setExpandedCompany(companyId);
    if (!companyUsers[companyId]) {
      try {
        const res = await api.get(`/admin/companies/${companyId}`);
        setCompanyUsers(prev => ({ ...prev, [companyId]: res.data.users || [] }));
      } catch (err) {
        console.error('Error fetching company users:', err);
      }
    }
  };

  const refreshCompanyUsers = async (companyId) => {
    try {
      const res = await api.get(`/admin/companies/${companyId}`);
      setCompanyUsers(prev => ({ ...prev, [companyId]: res.data.users || [] }));
    } catch (err) {
      console.error('Error refreshing company users:', err);
    }
  };

  const handlePromote = async (userId, companyId) => {
    try {
      await api.post(`/admin/companies/users/${userId}/promote`);
      await refreshCompanyUsers(companyId);
    } catch (err) { console.error('Error promoting user:', err); }
  };

  const handleDemote = async (userId, companyId) => {
    try {
      await api.post(`/admin/companies/users/${userId}/demote`);
      await refreshCompanyUsers(companyId);
    } catch (err) { console.error('Error demoting user:', err); }
  };

  // ── Delete user — super admin only ─────────────────────────────────────────
  const handleDeleteUser = async (userId, userName, companyId) => {
    if (!window.confirm(`Permanently delete user "${userName}"? This cannot be undone.`)) return;
    setDeletingUserId(userId);
    try {
      await api.delete(`/admin/users/${userId}`);
      // Remove from expanded list immediately
      setCompanyUsers(prev => ({
        ...prev,
        [companyId]: (prev[companyId] || []).filter(u => u.id !== userId),
      }));
      // Refresh company stats
      fetchData();
    } catch (err) {
      console.error('Error deleting user:', err);
      alert(err.response?.data?.message || 'Failed to delete user');
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);
    try {
      await api.post('/admin/companies', form);
      setShowAddModal(false);
      setForm(emptyForm);
      fetchData();
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to create company');
    } finally { setFormLoading(false); }
  };

  const openEdit = (company) => {
    setEditTarget(company);
    setForm({ name: company.name, namespace: company.namespace || '', plan: company.plan || 'standard', notes: company.notes || '' });
    setFormError('');
    setShowEditModal(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);
    try {
      await api.put(`/admin/companies/${editTarget.id}`, form);
      setShowEditModal(false);
      setEditTarget(null);
      setForm(emptyForm);
      fetchData();
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to update company');
    } finally { setFormLoading(false); }
  };

  const handleDeleteCompany = async (companyId, companyName) => {
    if (!window.confirm(`Delete "${companyName}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/companies/${companyId}`);
      fetchData();
    } catch (err) { console.error('Error deleting company:', err); }
  };

  const handleApprove = async (userId) => {
    try {
      await api.post(`/admin/companies/users/${userId}/approve`);
      setPendingUsers(prev => prev.filter(u => u.id !== userId));
    } catch (err) { console.error('Error approving user:', err); }
  };

  const handleReject = async (userId) => {
    try {
      await api.post(`/admin/companies/users/${userId}/reject`);
      setPendingUsers(prev => prev.filter(u => u.id !== userId));
    } catch (err) { console.error('Error rejecting user:', err); }
  };

  const cancelForm = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setForm(emptyForm);
    setFormError('');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      <AdminNav />
      <HomeButton />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold">Company Management</h2>
            <p className="text-slate-400 text-sm mt-1">Register companies and manage user access requests</p>
          </div>
          <button
            onClick={() => { setForm(emptyForm); setFormError(''); setShowAddModal(true); }}
            className="flex items-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-blue-600 hover:to-cyan-500 text-white font-medium rounded-lg transition-all text-sm">
            <Plus className="w-4 h-4" /><span>Register Company</span>
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center"><Building2 className="w-6 h-6 text-blue-400" /></div>
              <span className="text-2xl font-bold">{companies.length}</span>
            </div>
            <h3 className="text-slate-400 text-sm">Registered Companies</h3>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-yellow-500/10 rounded-lg flex items-center justify-center"><Clock className="w-6 h-6 text-yellow-400" /></div>
              <span className="text-2xl font-bold">{pendingUsers.length}</span>
            </div>
            <h3 className="text-slate-400 text-sm">Pending Approvals</h3>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center"><Users className="w-6 h-6 text-green-400" /></div>
              <span className="text-2xl font-bold">{companies.reduce((acc, c) => acc + (parseInt(c.active_users) || 0), 0)}</span>
            </div>
            <h3 className="text-slate-400 text-sm">Active Company Users</h3>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 mb-6 bg-slate-900 border border-slate-800 rounded-xl p-1 w-fit">
          <button onClick={() => setActiveTab('companies')}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'companies' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'text-slate-400 hover:text-white'}`}>
            <span className="flex items-center space-x-2"><Building2 className="w-4 h-4" /><span>Companies ({companies.length})</span></span>
          </button>
          <button onClick={() => setActiveTab('pending')}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'pending' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' : 'text-slate-400 hover:text-white'}`}>
            <span className="flex items-center space-x-2">
              <Clock className="w-4 h-4" />
              <span>Pending Approvals{pendingUsers.length > 0 && <span className="ml-1.5 px-1.5 py-0.5 bg-yellow-500 text-black text-xs rounded-full font-bold">{pendingUsers.length}</span>}</span>
            </span>
          </button>
        </div>

        {/* ── Tab: Companies ── */}
        {activeTab === 'companies' && (
          <div className="bg-slate-900 border border-slate-800 rounded-xl">
            {companies.length === 0 ? (
              <div className="py-16 text-center">
                <Building2 className="w-12 h-12 text-slate-700 mx-auto mb-4" />
                <p className="text-slate-400 mb-2">No companies registered yet</p>
                <button onClick={() => { setForm(emptyForm); setFormError(''); setShowAddModal(true); }}
                  className="px-4 py-2 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded-lg text-sm hover:bg-blue-500/30 transition-colors">
                  Register your first company
                </button>
              </div>
            ) : (
              <div className="divide-y divide-slate-800">
                {companies.map(company => (
                  <div key={company.id}>
                    <div className="p-5 hover:bg-slate-800/30 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 flex-1 min-w-0">
                          <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Building2 className="w-5 h-5 text-blue-400" />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                              <span className="font-semibold text-white">{company.name}</span>
                              <PlanBadge plan={company.plan} />
                              <StatusBadge status={company.status} />
                            </div>
                            <div className="flex items-center space-x-4 mt-1 text-sm text-slate-400 flex-wrap gap-y-1">
                              {company.email_domain && (
                              <span className="flex items-center space-x-1"><Globe className="w-3.5 h-3.5" /><span>@{company.email_domain}</span></span>
                              )}
                              {company.namespace && (
                                <span className="flex items-center space-x-1"><Package className="w-3.5 h-3.5" /><span className="font-mono text-xs bg-slate-800 px-1.5 py-0.5 rounded">{company.namespace}</span></span>
                              )}
                              <span className="flex items-center space-x-1">
                                <Users className="w-3.5 h-3.5" /><span>{company.active_users || 0} active</span>
                                {company.pending_users > 0 && <span className="text-yellow-400">· {company.pending_users} pending</span>}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 ml-4 flex-shrink-0">
                          <button onClick={() => openEdit(company)} className="p-2 text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors" title="Edit">
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDeleteCompany(company.id, company.name)} className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors" title="Delete company">
                            <Trash2 className="w-4 h-4" />
                          </button>
                          <button onClick={() => toggleExpand(company.id)} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors">
                            {expandedCompany === company.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* ── Expanded users list ── */}
                    {expandedCompany === company.id && (
                      <div className="px-5 pb-5 bg-slate-800/20">
                        <div className="border border-slate-700 rounded-xl overflow-hidden">
                          <div className="px-4 py-3 bg-slate-800/50 border-b border-slate-700 flex items-center justify-between">
                            <h4 className="text-sm font-medium text-slate-300">Users in {company.name}</h4>
                            {isSuperAdmin && (
                              <span className="text-xs text-slate-500 flex items-center gap-1">
                                <Shield className="w-3 h-3" /> Super admin — can delete users
                              </span>
                            )}
                          </div>
                          {!companyUsers[company.id] ? (
                            <div className="py-6 text-center text-slate-500 text-sm">Loading...</div>
                          ) : companyUsers[company.id].length === 0 ? (
                            <div className="py-6 text-center text-slate-500 text-sm">No users yet</div>
                          ) : (
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b border-slate-700 text-left">
                                  <th className="px-4 py-2.5 text-slate-400 font-medium">Name</th>
                                  <th className="px-4 py-2.5 text-slate-400 font-medium">Email</th>
                                  <th className="px-4 py-2.5 text-slate-400 font-medium">Status</th>
                                  <th className="px-4 py-2.5 text-slate-400 font-medium">Role</th>
                                  <th className="px-4 py-2.5 text-slate-400 font-medium">Joined</th>
                                  <th className="px-4 py-2.5 text-slate-400 font-medium">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {companyUsers[company.id].map(u => (
                                  <tr key={u.id} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
                                    <td className="px-4 py-3 text-white">{u.name}</td>
                                    <td className="px-4 py-3 text-slate-400">{u.email}</td>
                                    <td className="px-4 py-3"><StatusBadge status={u.status} /></td>
                                    <td className="px-4 py-3"><RoleBadge role={u.role} /></td>
                                    <td className="px-4 py-3 text-slate-400">{new Date(u.created_at).toLocaleDateString()}</td>
                                    <td className="px-4 py-3">
                                      <div className="flex items-center gap-2">
                                        {/* Promote / Demote */}
                                        {u.role === 'company_admin' ? (
                                          <button onClick={() => handleDemote(u.id, company.id)}
                                            className="flex items-center space-x-1 px-2 py-1 text-xs bg-slate-700 hover:bg-slate-600 text-slate-300 rounded transition-colors">
                                            <ShieldOff className="w-3 h-3" /><span>Demote</span>
                                          </button>
                                        ) : (
                                          <button onClick={() => handlePromote(u.id, company.id)}
                                            className="flex items-center space-x-1 px-2 py-1 text-xs bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 border border-purple-500/30 rounded transition-colors">
                                            <Shield className="w-3 h-3" /><span>Make Admin</span>
                                          </button>
                                        )}

                                        {/* Delete — super admin only, cannot delete other admins */}
                                        {isSuperAdmin && u.role !== 'admin' && (
                                          <button
                                            onClick={() => handleDeleteUser(u.id, u.name, company.id)}
                                            disabled={deletingUserId === u.id}
                                            className="flex items-center gap-1 px-2 py-1 text-xs bg-slate-700 text-slate-400 border border-slate-600 hover:bg-red-500/20 hover:text-red-400 hover:border-red-500/30 rounded transition-colors disabled:opacity-50"
                                            title="Delete user permanently"
                                          >
                                            {deletingUserId === u.id
                                              ? <div className="w-3 h-3 border border-slate-400/30 border-t-slate-400 rounded-full animate-spin" />
                                              : <Trash2 className="w-3 h-3" />
                                            }
                                            Delete
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Tab: Pending Approvals ── */}
        {activeTab === 'pending' && (
          <div className="bg-slate-900 border border-slate-800 rounded-xl">
            {pendingUsers.length === 0 ? (
              <div className="py-16 text-center">
                <CheckCircle className="w-12 h-12 text-green-500/50 mx-auto mb-4" />
                <p className="text-slate-400 mb-1">No pending approvals</p>
                <p className="text-slate-500 text-sm">All users have been reviewed</p>
              </div>
            ) : (
              <div>
                <div className="px-6 py-4 border-b border-slate-800">
                  <p className="text-sm text-slate-400">{pendingUsers.length} user{pendingUsers.length !== 1 ? 's' : ''} waiting for approval</p>
                </div>
                <div className="divide-y divide-slate-800">
                  {pendingUsers.map(u => (
                    <div key={u.id} className="p-5 flex items-center justify-between hover:bg-slate-800/30 transition-colors">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-medium text-sm">{u.name?.charAt(0)?.toUpperCase() || '?'}</span>
                        </div>
                        <div>
                          <p className="font-medium text-white">{u.name}</p>
                          <p className="text-sm text-slate-400">{u.email}</p>
                          <div className="flex items-center space-x-2 mt-1">
                            <Building2 className="w-3.5 h-3.5 text-slate-500" />
                            <span className="text-xs text-slate-500">{u.company_name}</span>
                            <span className="text-slate-600">·</span>
                            <span className="text-xs text-slate-500">Requested {new Date(u.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 ml-4 flex-shrink-0">
                        <button onClick={() => handleApprove(u.id)}
                          className="flex items-center space-x-1.5 px-3 py-2 bg-green-500/10 text-green-400 border border-green-500/30 hover:bg-green-500/20 rounded-lg transition-colors text-sm">
                          <CheckCircle className="w-4 h-4" /><span>Approve</span>
                        </button>
                        <button onClick={() => handleReject(u.id)}
                          className="flex items-center space-x-1.5 px-3 py-2 bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 rounded-lg transition-colors text-sm">
                          <XCircle className="w-4 h-4" /><span>Reject</span>
                        </button>
                        {/* Super admin can also delete pending users outright */}
                        {isSuperAdmin && (
                          <button
                            onClick={() => handleDeleteUser(u.id, u.name, u.company_id)}
                            disabled={deletingUserId === u.id}
                            className="flex items-center gap-1 px-3 py-2 bg-slate-800 text-slate-400 border border-slate-700 hover:bg-red-500/20 hover:text-red-400 hover:border-red-500/30 rounded-lg transition-colors text-sm disabled:opacity-50"
                          >
                            <Trash2 className="w-4 h-4" /><span>Delete</span>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {showAddModal && (
        <Modal title="Register New Company" onClose={cancelForm}>
          <CompanyForm form={form} setForm={setForm} formError={formError} formLoading={formLoading}
            onSubmit={handleCreate} submitLabel="Register Company" onCancel={cancelForm} />
        </Modal>
      )}
      {showEditModal && (
        <Modal title={`Edit — ${editTarget?.name}`} onClose={cancelForm}>
          <CompanyForm form={form} setForm={setForm} formError={formError} formLoading={formLoading}
            onSubmit={handleEdit} submitLabel="Save Changes" onCancel={cancelForm} />
        </Modal>
      )}
    </div>
  );
};

export default AdminCompanies;